import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useAssignmentHistory } from "../../hooks/useAssignmentHistory";
import { useAppTranslation } from "../../hooks/useAppTranslation";

type Props = {
  open: boolean;
  onClose: () => void;
  companyId: string;
  documentId: string;
  currentOwnerEmail?: string | null;
};

export default function AssignmentHistoryDialog({
  open,
  onClose,
  companyId,
  documentId,
  currentOwnerEmail,
}: Props) {
const { t } = useAppTranslation("common");
  const { items, hasMore, loading, loadMore } =
    useAssignmentHistory(companyId, documentId, open);

    const formatDate = (date?: string | null) => {
        if (!date) return "-";

        const d = new Date(date);

        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();

        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");

        return `${day}.${month}.${year} ${hours}:${minutes}`;
    };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { maxHeight: "70vh" },
      }}
    >
      <DialogTitle>{t("Historia zmian właściciela")}</DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
            <Box sx={{ maxHeight: "60vh", overflowY: "auto", px: 3, py: 2 }}>
                <Stack spacing={2.5}>
                {!loading && items.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                    {t("Brak historii zmian właściciela")}
                    </Typography>
                )}

                {items.map((item, index) => {
                const isLatest = index === 0;

                const isAssign = !item.previousUser?.email && item.newUser?.email;
                const isRemove = item.previousUser?.email && !item.newUser?.email;
                const isChange =
                    item.previousUser?.email && item.newUser?.email;

                const color = isAssign
                    ? "success.main"
                    : isRemove
                    ? "warning.main"
                    : "primary.main";

                const label = isAssign
                    ? t("Przypisano właściciela")
                    : isRemove
                    ? t("Usunięto właściciela")
                    : t("Zmieniono właściciela");

                const isCurrentOwner =
                    item.newUser?.email &&
                    item.newUser.email === currentOwnerEmail;

                const renderUserCompact = (user?: any | null, highlight = false) => {
                    if (!user?.email) return t("Nie przypisano");

                    return (
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                            variant="body2"
                            sx={{
                            fontWeight: highlight ? 700 : 500,
                            }}
                        >
                            {user.displayName || user.email}
                        </Typography>

                        {highlight && (
                            <Box
                            sx={{
                                px: 0.75,
                                py: 0.25,
                                fontSize: 10,
                                borderRadius: 1,
                                backgroundColor: "primary.main",
                                color: "white",
                            }}
                            >
                                {t("Aktualny")}
                            </Box>
                        )}
                        </Stack>

                        {user.displayName &&
                        user.displayName !== user.email && (
                            <Typography
                            variant="caption"
                            color="text.secondary"
                            >
                                {user.email}
                            </Typography>
                        )}
                    </Box>
                    );
                };

                return (
                    <Box
                    key={item.id}
                    sx={{
                        position: "relative",
                        pl: 4,
                    }}
                    >
                    {/* Linia */}
                    {index !== items.length - 1 && (
                        <Box
                        sx={{
                            position: "absolute",
                            left: 14,
                            top: 26,
                            bottom: -12,
                            width: 2,
                            backgroundColor: "divider",
                        }}
                        />
                    )}

                    {/* Kropka */}
                    <Box
                        sx={{
                        position: "absolute",
                        left: 8,
                        top: 18,
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: color,
                        }}
                    />

                    {/* Karta */}
                    <Box
                        sx={{
                        p: 1.75,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: isLatest ? "primary.main" : "divider",
                        backgroundColor: isLatest
                            ? "rgba(25, 118, 210, 0.04)"
                            : "background.paper",
                        transition: "all 0.2s ease",
                        }}
                    >
                        <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        >
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color }}
                        >
                            {label}
                        </Typography>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            {formatDate(item.changedAt)}
                        </Typography>
                        </Stack>

                        <Box sx={{ mt: 1 }}>
                        {isChange && (
                            <Stack direction="row" spacing={1} alignItems="center">
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {t("changed_from")}:
                            </Typography>
                            {renderUserCompact(item.previousUser)}
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                →
                            </Typography>
                            {renderUserCompact(
                                item.newUser,
                                isCurrentOwner
                            )}
                            </Stack>
                        )}

                        {isAssign && (
                            <Stack direction="row" spacing={1}>
                            {renderUserCompact(
                                item.newUser,
                                isCurrentOwner
                            )}
                            </Stack>
                        )}

                        {isRemove && (
                            <Stack direction="row" spacing={1}>
                            {renderUserCompact(item.previousUser)}
                            </Stack>
                        )}
                        </Box>

                        <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 1 }}
                        >
                        {t("changed_by")}:{" "}
                        {item.changedBy?.displayName ||
                            item.changedBy?.email}
                        </Typography>
                    </Box>
                    </Box>
                );
                })}

                {loading && (
                    <Box sx={{ textAlign: "center", py: 2 }}>
                    <CircularProgress size={22} />
                    </Box>
                )}

                {!loading && hasMore && (
                    <Button variant="outlined" size="small" onClick={loadMore}>
                    {t("Załaduj więcej")}
                    </Button>
                )}
                </Stack>
            </Box>
            </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t("Zamknij")}</Button>
      </DialogActions>
    </Dialog>
  );
}